/**
 * CommunicationContextPanel — the right column of the reading zone.
 *
 * A PURE RENDERER of the CommunicationContext read model. It contains NO business
 * logic: no computing outstanding, no deciding what needs attention, no deadline
 * maths. Every value is already assembled + formatted by the Communication
 * Context Engine (source-tagged, authoritative-flagged). This component only does
 * layout, grouping, and navigation.
 *
 * Sections render in TRUST order — Matter (what is this?) → Facts (what's
 * objectively true?) → Attention (what should I notice?) → Signals → Relationships.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Scales, Bank, PushPin, Warning, ArrowSquareOut, LinkSimple } from '@phosphor-icons/react';
import {
  fetchConversationContext, trackContextEvent,
  type CommunicationContext, type ContextItem, type ContextSource,
} from '@/lib/inboxApi';

const FONT = "'IBM Plex Sans', sans-serif";

interface Props {
  conversationId: string;
  /** Jump to the record behind a fact (trace). */
  onNavigate?: (entity: string, id: string) => void;
  /** Offered when the conversation is not yet linked to a matter. */
  onLinkMatter?: () => void;
}

function relAgo(iso: string | null): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const SOURCE_LABEL: Record<ContextSource, string> = {
  Matter: 'Matter', MoneyFacts: 'Money', MatterActivity: 'Activity', Hearings: 'Hearing',
  StrategicAwareness: 'Awareness', Relationship: 'Relationship', LegalSignals: 'Signal', Conversation: 'Thread',
};

function SourceChip({ source }: { source: ContextSource }) {
  return (
    <span style={{
      fontFamily: FONT, fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
      color: 'var(--text-subtle)', background: 'var(--surface-sunken)', borderRadius: 3, padding: '1px 4px',
    }}>
      {SOURCE_LABEL[source]}
    </span>
  );
}

function FactRow({ item, onNavigate }: { item: ContextItem; onNavigate?: Props['onNavigate'] }) {
  const clickable = !!(item.trace && onNavigate);
  return (
    <div
      onClick={clickable ? () => onNavigate!(item.trace!.entity, item.trace!.id) : undefined}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 8, padding: '5px 0',
        borderBottom: '1px solid var(--border-subtle)', cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <span style={{ fontFamily: FONT, fontSize: 11, color: 'var(--text-subtle)', width: 84, flexShrink: 0 }}>
        {item.title}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 500 }}>
        {item.value}
        {clickable && <ArrowSquareOut size={11} style={{ marginLeft: 5, verticalAlign: 'middle', color: 'var(--text-subtle)' }} />}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {!item.authoritative && item.confidence && (
          <span style={{ fontFamily: FONT, fontSize: 9, fontWeight: 600, color: '#7c3aed' }}>{item.confidence}</span>
        )}
        {item.lastUpdatedAt && (
          <span style={{ fontFamily: FONT, fontSize: 10, color: 'var(--text-subtle)' }} title={`Updated ${new Date(item.lastUpdatedAt).toLocaleString('en-GB')}`}>
            {relAgo(item.lastUpdatedAt)}
          </span>
        )}
        <SourceChip source={item.source} />
      </span>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-default)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {icon}
        <span style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-subtle)' }}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

export function CommunicationContextPanel({ conversationId, onNavigate, onLinkMatter }: Props) {
  const { data: ctx, isLoading } = useQuery<CommunicationContext>({
    queryKey: ['comm-context', conversationId],
    queryFn: () => fetchConversationContext(conversationId),
    enabled: !!conversationId,
    staleTime: 30_000,
  });

  // Telemetry — fire once per conversation once the context is assembled.
  useEffect(() => {
    if (ctx) trackContextEvent('panel_opened', { linked: !!ctx.sections.matter });
  }, [conversationId, ctx]);

  function handleNavigate(entity: string, id: string) {
    trackContextEvent('trace_clicked', { source: entity });
    onNavigate?.(entity, id);
  }
  function handleLinkMatter() {
    trackContextEvent('link_matter_clicked');
    onLinkMatter?.();
  }

  if (isLoading || !ctx) {
    return <div style={{ padding: 16, fontFamily: FONT, fontSize: 12, color: 'var(--text-subtle)' }}>Assembling context…</div>;
  }

  const { matter, facts, signals, relationships, attention } = ctx.sections;
  const allFacts = [...facts.money, ...facts.activity, ...facts.hearing];
  const rel = [...relationships.instructingSolicitor, ...relationships.layClient];

  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>
      {/* 1 — Matter (what is this?) */}
      <Section icon={<Scales size={13} color="#2563eb" />} title="Matter">
        {matter ? (
          <div style={{ fontFamily: FONT }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{matter.caseNumber}</span>
              <span style={{ fontSize: 10, color: 'var(--text-subtle)', textTransform: 'capitalize' }}>{matter.status.replace(/_/g, ' ')}</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 1 }}>{matter.caseName}</div>
            <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: 11.5 }}>
              {matter.court && (<><span style={{ color: 'var(--text-subtle)' }}>Court</span><span style={{ color: 'var(--text-primary)' }}>{matter.court}</span></>)}
              {matter.client && (<><span style={{ color: 'var(--text-subtle)' }}>Client</span><span style={{ color: 'var(--text-primary)' }}>{matter.client}</span></>)}
              {matter.solicitor && (<><span style={{ color: 'var(--text-subtle)' }}>Solicitor</span><span style={{ color: 'var(--text-primary)' }}>{matter.solicitor}</span></>)}
              {matter.opponent && (<><span style={{ color: 'var(--text-subtle)' }}>Opponent</span><span style={{ color: 'var(--text-primary)' }}>{matter.opponent}</span></>)}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--text-subtle)', marginBottom: 8 }}>Not linked to a matter.</div>
            {onLinkMatter && (
              <button
                onClick={handleLinkMatter}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: '#2563eb', background: 'none', border: '1px solid var(--border-default)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' }}
              >
                <LinkSimple size={13} /> Link to matter
              </button>
            )}
          </div>
        )}
      </Section>

      {/* 2 — Facts (what's objectively true?) */}
      {allFacts.length > 0 && (
        <Section icon={<Bank size={13} color="#059669" />} title="Facts">
          {allFacts.map((item, i) => <FactRow key={`f${i}`} item={item} onNavigate={handleNavigate} />)}
        </Section>
      )}

      {/* Conversation timeline facts (kept distinct — thread, not matter) */}
      {facts.conversation.length > 0 && (
        <Section icon={<PushPin size={13} color="#6b7280" />} title="This conversation">
          {facts.conversation.map((item, i) => <FactRow key={`c${i}`} item={item} onNavigate={handleNavigate} />)}
        </Section>
      )}

      {/* 3 — Attention (what should I notice?) */}
      {attention.length > 0 && (
        <Section icon={<Warning size={13} weight="fill" color="#d97706" />} title="Requires attention">
          {attention.map((a, i) => (
            <div key={`a${i}`} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{a.label}</div>
              <div style={{ fontFamily: FONT, fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 1 }}>{a.reason}</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                {a.contributors.map((c) => <SourceChip key={c} source={c} />)}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* 4 — Signals (A2) */}
      {signals.length > 0 && (
        <Section icon={<Scales size={13} color="#7c3aed" />} title="Signals">
          {signals.map((item, i) => <FactRow key={`s${i}`} item={item} onNavigate={handleNavigate} />)}
        </Section>
      )}

      {/* 5 — Relationships (A3) */}
      {rel.length > 0 && (
        <Section icon={<Bank size={13} color="#0891b2" />} title="Relationship">
          {rel.map((item, i) => <FactRow key={`r${i}`} item={item} onNavigate={handleNavigate} />)}
        </Section>
      )}
    </div>
  );
}
